import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {IEmailAttachment} from '@/services/helpdeskEmailAttachments';
import {RootState} from '../RootState';
import {settings} from '@/plugins/settings';
import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import responseHandler from '@/plugins/errorHandling';

export interface IHelpdeskSentEmail {
  id: number;
  gmail_account_id: number;
  in_reply_to_email_id: number | null;
  to_email: string;
  cc_emails: string[] | null;
  bcc_emails: string[] | null;
  subject: string;
  body: string;
  thread_id: string | null;
  gmail_message_id: string | null;
  sent_by: string | null;
  sent_at: string;
  gmail_account?: {id: number; email: string};
  reply_to_email?: {id: number; from_email: string; subject: string} | null;
}

export interface IThreadItem {
  _type: 'received' | 'sent';
  id: number;
  date: string;
  from_email?: string;
  to_email?: string;
  sent_by?: string | null;
  subject: string;
  body: string;
  cc_emails?: string[] | null;
  bcc_emails?: string[] | null;
  attachments?: IEmailAttachment[] | null;
}

interface SentState {
  emails: IHelpdeskSentEmail[];
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  threadEmails: IHelpdeskSentEmail[];
  fullThread: IThreadItem[];
  // Email whose thread is expected: a late thread of a previously clicked email is discarded.
  fullThreadForEmailId: number | null;
}

const state: SentState = {
  emails: [],
  currentPage: 1,
  lastPage: 1,
  total: 0,
  perPage: 15,
  threadEmails: [],
  fullThread: [],
  fullThreadForEmailId: null,
};

const getters: GetterTree<SentState, RootState> = {
  emails:       (state) => state.emails,
  currentPage:  (state) => state.currentPage,
  lastPage:     (state) => state.lastPage,
  total:        (state) => state.total,
  perPage:      (state) => state.perPage,
  threadEmails: (state) => state.threadEmails,
  fullThread:   (state) => state.fullThread,
};

const mutations: MutationTree<SentState> = {
  errors(_state, _payload: any) {},
  emailComposed(_state, _payload: any) {},
  sentLoaded(state, payload: any) {
    state.emails      = payload.data;
    state.currentPage = payload.current_page;
    state.lastPage    = payload.last_page;
    state.total       = payload.total;
    state.perPage     = payload.per_page;
  },
  threadLoaded(state, payload: any) {
    state.threadEmails = payload.data;
  },
  threadCleared(state) {
    state.threadEmails = [];
    state.fullThread   = [];
  },
  fullThreadRequested(state, emailId: number) {
    // Switching email: don't keep showing the previous thread while the new one loads.
    // Same email (refresh after a reply): keep it on screen to avoid a flicker.
    if (state.fullThreadForEmailId !== emailId) state.fullThread = [];
    state.fullThreadForEmailId = emailId;
  },
  fullThreadLoaded(state, payload: {received: any[]; sent: any[]; _forEmailId?: number}) {
    if (payload._forEmailId !== undefined && payload._forEmailId !== state.fullThreadForEmailId) return;
    const received: IThreadItem[] = (payload.received || []).map((e: any) => ({
      _type:     'received' as const,
      id:        e.id,
      date:      e.received_at,
      from_email: e.from_email,
      subject:   e.subject,
      body:      e.body,
      attachments: e.attachments,
    }));
    const sent: IThreadItem[] = (payload.sent || []).map((e: any) => ({
      _type:     'sent' as const,
      id:        e.id,
      date:      e.sent_at,
      to_email:  e.to_email,
      sent_by:   e.sent_by,
      subject:   e.subject,
      body:      e.body,
      cc_emails:  e.cc_emails,
      bcc_emails: e.bcc_emails,
    }));
    state.fullThread = [...received, ...sent].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  },
};

const actions: ActionTree<SentState, RootState> = {
  fetchThreadReplies: ({commit}, emailId: number) => {
    const query = new URLSearchParams();
    query.set('email_id', String(emailId));
    query.set('per_page', '100');
    return responseHandler.handle(
      () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/sent?' + query.toString(), method: 'GET'}),
      commit, 'threadLoaded',
    );
  },
  clearThread: ({commit}) => { commit('threadCleared'); },
  composeEmail: ({commit}, data: {account_id?: number; to_email: string; subject: string; body: string; cc?: string[]; bcc?: string[]; sent_by?: string; signature_body?: string}) => responseHandler.handle(
    () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/compose', method: 'POST', data}),
    commit, 'emailComposed', null, false,
  ),
  fetchFullThread: ({commit}, emailId: number) => {
    commit('fullThreadRequested', emailId);
    return responseHandler.handle(
      () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + emailId + '/thread', method: 'GET'})
        .then((res: any) => { if (res.data && res.data.data) res.data.data._forEmailId = emailId; return res; }),
      commit, 'fullThreadLoaded',
    );
  },
  fetchSent: ({commit}, params?: {page?: number; per_page?: number; search?: string; filter_to?: string; filter_sent_by?: string; filter_cc?: string}) => {
    const query = new URLSearchParams();
    if (params && params.page)          query.set('page',          String(params.page));
    if (params && params.per_page)      query.set('per_page',      String(params.per_page));
    if (params && params.search)        query.set('search',        params.search);
    if (params && params.filter_to)     query.set('filter_to',     params.filter_to);
    if (params && params.filter_sent_by) query.set('filter_sent_by', params.filter_sent_by);
    if (params && params.filter_cc)     query.set('filter_cc',     params.filter_cc);
    const qs = query.toString() ? '?' + query.toString() : '';
    return responseHandler.handle(
      () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/sent' + qs, method: 'GET'}),
      commit, 'sentLoaded',
    );
  },
};

export const helpdeskSentModule: Module<SentState, RootState> = {
  namespaced: true, state, getters, mutations, actions,
};
